import { INTERCOM_WIDGET_URL } from "./constants";

/** `window.Intercom`: the messenger once loaded, or the queue stub before it. */
type TIntercom = ((method: string, ...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
  interface Window {
    Intercom?: TIntercom;
  }
}

/** Intercom's standard snippet stub: calls made before the widget script runs
 *  are queued on `Intercom.q`, which the script replays when it loads. */
export function installIntercomStub(): void {
  if (typeof window.Intercom === "function") return;
  const queue: unknown[][] = [];
  const stub: TIntercom = (...args: unknown[]) => {
    queue.push(args);
  };
  stub.q = queue;
  window.Intercom = stub;
}

/** Call the messenger if it (or its stub) is there; otherwise do nothing. A
 *  throw from it (a broken widget, an extension) is logged, never passed on:
 *  callers include sign-out and the expired-session redirect. */
export function callIntercom(method: string, ...args: unknown[]): void {
  try {
    window.Intercom?.(method, ...args);
  } catch (error) {
    console.warn("[intercom] the messenger threw", { method, error });
  }
}

let loading: Promise<void> | null = null;

/** Add the widget script once per page, after the page itself has loaded so it
 *  never competes with NextGen. Rejects when it is blocked (an ad blocker, a
 *  company network); the failed tag is removed so a later call can try again. */
export function loadIntercomScript(appId: string): Promise<void> {
  loading ??= afterPageLoad().then(
    () =>
      new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.async = true;
        script.src = `${INTERCOM_WIDGET_URL}${encodeURIComponent(appId)}`;
        script.addEventListener("load", () => resolve(), { once: true });
        script.addEventListener(
          "error",
          () => {
            script.remove();
            loading = null;
            reject(new Error(`Intercom's script didn't load from ${script.src}`));
          },
          { once: true },
        );
        document.head.appendChild(script);
      }),
  );
  return loading;
}

function afterPageLoad(): Promise<void> {
  if (document.readyState === "complete") return Promise.resolve();
  return new Promise((resolve) => window.addEventListener("load", () => resolve(), { once: true }));
}
